/* vx.js — ONE reveal observer for the whole page.
 *
 * Why this file exists: the observer used to live inside vx-buybox and only observed
 * `.vx-reveal` nodes *within that section*. Every other vx section sets `data-vx-reveal="on"`
 * on itself, which is what applies `opacity:0` — so mechanism, outcomes, compare, guarantee and
 * FAQ were rendered, counted by the QA gate, and permanently invisible. A per-section observer
 * cannot be correct here: the CSS is global, so the observer must be too.
 *
 * Failure posture: if anything here throws or IntersectionObserver is missing, the
 * `data-vx-reveal` attribute is removed, which restores full opacity. Content is never left
 * hidden behind a script.
 */
(() => {
  const start = () => {
    const roots = [...document.querySelectorAll('[data-vx-reveal="on"]')];
    if (!roots.length) return;

    const unhide = () => roots.forEach((r) => r.removeAttribute('data-vx-reveal'));

    if (!('IntersectionObserver' in window)) return unhide();

    try {
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (!e.isIntersecting) return;
            e.target.classList.add('is-in');
            io.unobserve(e.target);
          });
        },
        /* -6% bottom margin so a reveal fires slightly before the element is fully on screen;
           a tight threshold is a known way to make reveals silently never run on short sections */
        { threshold: 0.08, rootMargin: '0px 0px -6% 0px' },
      );

      roots.forEach((root) => {
        const items = [...root.querySelectorAll('.vx-reveal')];
        items.forEach((el, i) => {
          /* stagger is per-section, not per-page: a global index would give the last section a
             multi-second delay and read as broken */
          el.style.transitionDelay = Math.min(i, 8) * 55 + 'ms';
          io.observe(el);
        });
        /* anything already in view on load should not wait for a scroll that may never come */
        requestAnimationFrame(() => {
          items.forEach((el) => {
            const r = el.getBoundingClientRect();
            if (r.top < innerHeight && r.bottom > 0) el.classList.add('is-in');
          });
        });
      });
    } catch {
      unhide();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
